import React from 'react';
import { useHistory } from 'react-router-dom';
import { NamedLink, StrapiContentRenderer } from '../../../components';
import IconArrowTop from '../../../components/IconArrowTop/IconArrowTop';
import { getImagesLink } from '../../../util/dataExtractors';
import moment from 'moment';
import { isArrayLength } from '../../../util/genericHelpers';
import { createSlug } from '../../../util/urlHelpers';
import routeConfiguration from '../../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../../util/routes';
import { blockText } from '../../SingleBlogPage/articleEnhancements';

import css from '../BlogPage.module.css';
import { draftSlug } from '../../../routing/routeConfiguration';

const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

// Build a short plain-text excerpt from the Strapi description blocks (or an
// htmlDescription string) for the card. SSR-safe (no DOM).
const buildExcerpt = (description, htmlDescription, maxLen = 140) => {
  let text = '';
  if (Array.isArray(description)) {
    // Prefer the first non-empty paragraph. Some articles have a description
    // array whose only/first paragraph is empty (real content lives in
    // htmlDescription) — so require the paragraph to actually yield text before
    // using it, and otherwise fall through to the htmlDescription fallback.
    const firstPara = description.find(
      b => b && b.type === 'paragraph' && (blockText(b) || '').trim()
    );
    text = firstPara ? blockText(firstPara) : description.map(blockText).join(' ');
  }
  // Fallback: if the blocks produced nothing usable, strip tags off the raw
  // HTML description (covers articles whose description blocks are empty).
  if (!(text || '').replace(/\s+/g, ' ').trim() && typeof htmlDescription === 'string') {
    text = htmlDescription.replace(/<[^>]*>/g, ' ');
  }
  text = (text || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLen).trim()}\u2026`;
};

function BlogCard(props) {
  const { blog, showDate = true, showExcerpt = false } = props;
  const history = useHistory();

  const blogData = blog?.attributes || blog;
  const blogImages =
    blogData?.coverImage?.url || blogData?.coverImage?.data?.attributes?.url || null;
  const tag =
    blogData?.tag?.data?.attributes?.name ||
    blogData?.tag?.name ||
    blogData?.tag?.attributes?.name;
  const title = blogData?.title;
  const createdAt = blogData?.createdAt;
  const description = blogData?.description;
  const htmlDescription = blogData?.htmlDescription;
  const slug = blogData?.slug;
  const excerpt = showExcerpt ? buildExcerpt(description, htmlDescription) : '';
  const imagesLink = getImagesLink([blogImages], baseUrl);
  const tagSlug = tag ? createSlug(tag) : null;
  const tagParam = tagSlug && tagSlug !== 'no-slug' ? tagSlug : null;

  const onTagClick = event => {
    event.preventDefault();
    event.stopPropagation();
    if (tagParam) {
      history.push(
        createResourceLocatorString('BlogPage', routeConfiguration(), {}, { tag: tagParam })
      );
    }
  };

  const onTagKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      onTagClick(event);
    }
  };

  return (
    <NamedLink className={css.blog} name="SingleBlogPage" params={{ title: slug || draftSlug }}>
      <div className={css.blogImg}>
        {isArrayLength(imagesLink) && <img src={imagesLink[0]} alt={title} />}
      </div>
      <div className={css.blogDetails}>
        <div className={css.blogPostTime}>
          <div className={css.tagContainer}>
            {tag && tagParam ? (
              <span
                className={`${css.blogTime} ${css.tagPill} ${css.tagLink}`}
                role="link"
                tabIndex={0}
                onClick={onTagClick}
                onKeyDown={onTagKeyDown}
              >
                {tag}
              </span>
            ) : null}
          </div>
          {showDate ? (
            <span className={css.blogTime}>{moment(createdAt).format('DD/MM/YYYY')}</span>
          ) : null}
        </div>
        <div className={css.cardTitleSec}>
          <h2 className={css.blogPostTitle}>{title}</h2>
          <IconArrowTop />
        </div>
        {showExcerpt && excerpt ? <p className={css.blogPostExcerpt}>{excerpt}</p> : null}
      </div>
    </NamedLink>
  );
}

export default BlogCard;
